/**
 * Input Group Field Component
 * Responsible for showing input level errors specific to a particular field
 *
 */
import {
  Component,
  ContentChild,
  HostBinding,
  Inject
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { Subject } from 'rxjs';

import { InputErrorsComponent } from '../input-errors/input-errors.component';
import { ValueAccessorProviderFactory } from '../../value-accessor/value-accessor-provider-factory';
import { BaseValueAccessor } from '../../value-accessor/base-value-accessor';
import { FormComponent } from '../form.component';
import { InputStatus } from '../input-status/input-status.enum';

import type { AfterContentInit } from '@angular/core';
import type { FormError } from '../domain/form.domain';

@Component({
  selector: 'td-wb-input-group-field',
  templateUrl: 'input-group-field.component.html',
  styleUrls: ['input-group-field.component.scss'],
  providers: [ValueAccessorProviderFactory(InputGroupFieldComponent)],
  imports: [NgTemplateOutlet]
})
export class InputGroupFieldComponent extends BaseValueAccessor<Record<string, unknown>> implements  AfterContentInit {
  @ContentChild(NgModel) private inputControl: NgModel;
  @ContentChild(InputErrorsComponent) private inputErrors: InputErrorsComponent;
  name: string;
  onErrorSubject: Subject<FormError>;

  @HostBinding('class.td-wb-input-group-field--narrative')
  get isNarrative(): boolean {
    return this.form.formType === 'narrative';
  }

  constructor(@Inject(FormComponent) private form: FormComponent) {
    super();
    this.onErrorSubject = new Subject<FormError>();
  }

  ngAfterContentInit(): void {
    if (this.inputControl !== undefined) {
      this.initModelChangeHandler();
    }
  }

  getInputControl(): NgModel {
    return this.inputControl;
  }

  shouldForceDisplayError(): boolean {
    if (this.inputErrors !== undefined && this.inputControl !== undefined) {
      this.inputErrors.getErrorToDisplay(this.inputControl.errors);
      return this.inputErrors.shouldForceDisplay();
    }
  }

  isSubmitted(): boolean {
    if (this.inputErrors !== undefined && this.inputControl !== undefined) {
      return this.inputErrors.isSubmitted();
    }
  }

  initModelChangeHandler(): void {
    this.name = this.inputControl.name;

    if (this.inputErrors !== undefined) {
      this.inputErrors.subscribeToStatusChange(this.inputControl);
    }

    this.inputControl.statusChanges.subscribe((status: string): void => {
      if (this.inputControl.dirty || this.shouldForceDisplayError()) {
        this.toggleError(status === InputStatus.INVALID);
      } else {
        this.toggleError(false);
      }
    });
  }

  registerInput(inputModel: NgModel): void {
    this.inputControl = inputModel;
    this.initModelChangeHandler();
  }

  private toggleError(value: boolean): void {
    this.onErrorSubject.next({
      name: this.name,
      value
    });
  }
}
